/**
 * @file Registry-aware board-control definitions and shared render helpers.
 */

import { getEffectiveViewLayout } from "../../app/state/state.js";
import { getBoardRegistryEntry } from "./boardRegistry.js";
import { getBoardTableColumnDefinitions } from "./boardTableColumns.js";

export const DATE_MODE_LABELS = Object.freeze({
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
});

export const CONTROL_ICON_MAP = Object.freeze({
  group: "grid_view",
  sort: "swap_vert",
  filter: "filter_alt",
  columns: "table_edit",
});

export const ACTIVE_BORDER_CLASS = "border-[var(--dfc-brand-accent)]";
export const ACTIVE_TEXT_CLASS = "text-[var(--dfc-brand-accent)]";

/**
 * Returns the singular item type for a board view key.
 * @param {string} viewKey - Board view key.
 * @returns {string} Singular item type.
 */
export function getItemType(viewKey) {
  return getBoardRegistryEntry(viewKey)?.itemType || "";
}

/**
 * Escapes text for board-control HTML snippets.
 * @param {*} value - Value to escape.
 * @returns {string} Escaped text.
 */
export function escapeHtml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("\"", "&quot;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
}

function getColumnOrder(columnKey) {
  if (columnKey === "types") {return 200;}
  if (columnKey === "priority") {return 300;}
  if (columnKey === "status" || columnKey === "equipment_status") {return 400;}
  if (
    columnKey === "owner" ||
    columnKey === "case_manager" ||
    columnKey === "project_manager"
  ) {return 500;}
  if (columnKey === "assigned_users" || columnKey === "provider") {return 600;}
  if (
    columnKey === "due_date" ||
    columnKey === "next_action_required" ||
    columnKey === "next_appointment" ||
    columnKey === "delivery_date" ||
    columnKey === "next_due_date" ||
    columnKey === "next_service_due" ||
    columnKey === "purchase_date" ||
    columnKey === "first_seen"
  ) {return 700;}
  return 100;
}

/**
 * Returns table column entries in the board-control display order.
 * @param {string} viewKey - Board view key.
 * @returns {Array<[string, string]>} Ordered column entries.
 */
export function getOrderedColumnEntries(viewKey) {
  const columns = getBoardTableColumnDefinitions(viewKey);
  return Object.entries(columns).sort(([keyA, labelA], [keyB, labelB]) => {
    const orderDiff = getColumnOrder(keyA) - getColumnOrder(keyB);
    if (orderDiff !== 0) {return orderDiff;}
    return labelA.localeCompare(labelB);
  });
}

/**
 * Returns shared icon-button classes.
 * @param {boolean} [isActive] - Whether the button is active.
 * @returns {string} Class string.
 */
export function getIconButtonClasses(isActive = false) {
  return [
    "relative inline-flex h-10 w-10 items-center justify-center rounded-full",
    "border bg-white shadow-sm transition-colors",
    "hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)]",
    isActive ? `${ACTIVE_BORDER_CLASS} ${ACTIVE_TEXT_CLASS}` : "border-gray-200 text-gray-500",
  ].filter(Boolean).join(" ");
}

/**
 * Applies active/inactive visual state to a control button.
 * @param {HTMLElement|null} button - Button element.
 * @param {boolean} isActive - Whether the button is active.
 * @returns {void}
 */
export function applyControlButtonActiveState(button, isActive) {
  if (!button) {return;}
  button.classList.toggle(ACTIVE_BORDER_CLASS, isActive);
  button.classList.toggle(ACTIVE_TEXT_CLASS, isActive);
  button.classList.toggle("border-gray-200", !isActive);
  button.classList.toggle("text-gray-500", !isActive);
  button.style.borderColor = "";
  button.style.color = "";
}

/**
 * Returns the layout toggle icon for the current board layout.
 * @param {string} viewKey - Board view key.
 * @returns {string} Material Symbols icon name.
 */
export function getLayoutToggleIcon(viewKey) {
  return getEffectiveViewLayout(viewKey) === "table" ? "view_kanban" : "table";
}
